// Main budget (V33-V35, E06-E07), status workflow (X11), mismatch check (rule 11), change orders (rule 17).
import { useMutation } from "convex/react";
import { useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { api } from "../../../convex/_generated/api";
import type { Doc, Id } from "../../../convex/_generated/dataModel";
import { useProject } from "@/hooks/project";
import { Badge, Button, Card, Field, Input, Modal, PageHeader, Select, Tabs, cn, fmtNum, useRun } from "@/components/ui";
import { ExportButton, ImportButton } from "@/components/Importer";
import { exportBudget, exportChangeOrders, parseChangeOrders } from "@/lib/formats";
import { MappedImportButton } from "@/components/ColumnMapper";
import { BUDGET_FIELDS } from "@/lib/importSchemas";
import { num, str } from "@/lib/mapping";
import { currentWeight } from "@/engine/series";

type Tab = "matrix" | "check" | "cos";

export function BudgetPage() {
  const { project, b } = useProject();
  const [tab, setTab] = useState<Tab>("matrix");
  const pendingCos = b.changeOrders.filter((c) => c.status === "pending").length;
  return (
    <div className="space-y-4">
      <PageHeader title="Budget & change orders" sub="The budget table must match the deliverables. Change orders take effect in the period they're approved; earlier periods never change." actions={<StatusBar />} />
      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          { value: "matrix", label: "Main budget" },
          { value: "check", label: "Budget vs deliverables" },
          { value: "cos", label: `Change orders${pendingCos ? ` (${pendingCos} pending)` : ""}` },
        ]}
      />
      {tab === "matrix" && <Matrix key={project._id} />}
      {tab === "check" && <Check />}
      {tab === "cos" && <ChangeOrders />}
    </div>
  );
}

function StatusBar() {
  const { project, canEdit, isAdmin } = useProject();
  const setStatus = useMutation(api.budget.setStatus);
  const run = useRun();
  // One lock only (Cesar's test, 22:27): the budget is locked by locking the baseline on Periods & baseline.
  const order = ["draft", "checked", "reviewed"] as const;
  const i = project.baselineLocked ? 3 : order.indexOf(project.budgetStatus as (typeof order)[number]);
  const next = order[i + 1];
  void isAdmin;
  return (
    <div className="flex items-center gap-2">
      {order.map((s, k) => (
        <Badge key={s} tone={k < i ? "green" : k === i ? "blue" : "slate"} className={cn(k === i && "ring-1 ring-brand-500")}>
          {s}
        </Badge>
      ))}
      <Badge tone={project.baselineLocked ? "amber" : "slate"} title="The budget locks when the baseline is locked (Periods & baseline)">
        {project.baselineLocked ? "locked with baseline" : "locks with the baseline"}
      </Badge>
      {canEdit && next && !project.baselineLocked && (
        <Button size="sm" variant="primary" onClick={() => run(() => setStatus({ projectId: project._id, status: next }), `Budget ${next}`)}>
          Mark {next}
        </Button>
      )}
    </div>
  );
}

/** Budget cell values including approved budget change orders up to the current period. */
function useCurrentBudget() {
  const { b, periods, currentIdx } = useProject();
  return useMemo(() => {
    const cur = periods[currentIdx]?.number ?? Infinity;
    const key = (pbs: string | undefined, dbs: string, wbs: string) => `${pbs ?? ""}|${dbs}|${wbs}`;
    const orig = new Map<string, number>();
    for (const c of b.budgetCells) orig.set(key(c.pbs, c.dbs, c.wbs), (orig.get(key(c.pbs, c.dbs, c.wbs)) ?? 0) + c.hours);
    const cos = new Map<string, number>();
    for (const co of b.changeOrders)
      if (co.kind === "budget" && co.status === "approved" && co.periodNumber <= cur && co.dbs) {
        const k = key(co.pbs, co.dbs, co.wbs ?? "");
        cos.set(k, (cos.get(k) ?? 0) + (co.delta ?? 0));
      }
    return { orig, cos, key };
  }, [b, periods, currentIdx]);
}

function Matrix() {
  const { project, b, nodes, canEdit } = useProject();
  const setCells = useMutation(api.budget.setCells);
  const run = useRun();
  const { orig, cos, key } = useCurrentBudget();
  const [pbs, setPbs] = useState("");
  const locked = project.baselineLocked || project.budgetStatus === "locked";
  const wbsCols = useMemo(() => {
    const used = new Set(b.budgetCells.map((c) => c.wbs));
    const cols = nodes.wbs.map((n) => n.code);
    if (used.has("") || !cols.length) cols.push("");
    return cols;
  }, [nodes.wbs, b.budgetCells]);
  const val = (m: Map<string, number>, d: string, w: string) => {
    if (pbs) return m.get(key(pbs, d, w)) ?? 0;
    let s = 0;
    for (const [k, v] of m) {
      const [, dd, ww] = k.split("|");
      if (dd === d && ww === w) s += v;
    }
    return s;
  };
  const cell = (d: string, w: string) => val(orig, d, w) + val(cos, d, w);
  const rowTotal = (d: string) => wbsCols.reduce((s, w) => s + cell(d, w), 0);
  const colTotal = (w: string) => nodes.dbs.reduce((s, d) => s + cell(d.code, w), 0);
  const grand = nodes.dbs.reduce((s, d) => s + rowTotal(d.code), 0);
  const origTotal = [...orig.values()].reduce((s, v) => s + v, 0);
  const coTotal = [...cos.values()].reduce((s, v) => s + v, 0);
  return (
    <Card
      title="Main budget (hours)"
      actions={
        <>
          <Select value={pbs} onChange={(e) => setPbs(e.target.value)} className="w-44" aria-label="Phase">
            <option value="">All phases</option>
            {nodes.pbs.map((n) => (
              <option key={n.code} value={n.code}>
                {n.code} · {n.title}
              </option>
            ))}
          </Select>
          <ExportButton file={`${project.code}-budget.xlsx`} sheets={() => [exportBudget(b.budgetCells.map((c) => ({ pbs: c.pbs, dbs: c.dbs, wbs: c.wbs, hours: c.hours })))]} />
          {canEdit && !locked && (
            <MappedImportButton
              fields={BUDGET_FIELDS}
              rememberKey="budget"
              build={(mapped) => {
                const errors = mapped.filter((m) => !str(m.values.dbs) || num(m.values.hours) === null).map((m) => ({ line: m.line, message: "Needs a DBS code and hours" }));
                const cells = mapped
                  .filter((m) => str(m.values.dbs) && num(m.values.hours) !== null)
                  .map((m) => ({ dbs: str(m.values.dbs), wbs: str(m.values.wbs), hours: num(m.values.hours)!, ...(str(m.values.pbs) ? { pbs: str(m.values.pbs) } : {}) }));
                const dbs = new Set(nodes.dbs.map((n) => n.code));
                const wbs = new Set(nodes.wbs.map((n) => n.code));
                return {
                  problems: errors.map((e) => `Row ${e.line}: ${e.message}`),
                  note: <p className="text-sm text-slate-600">This replaces the whole main budget.</p>,
                  rows: cells.map((c, i) => {
                    const bad = [!dbs.has(c.dbs) && `DBS ${c.dbs} not in the breakdown`, c.wbs && !wbs.has(c.wbs) && `WBS ${c.wbs} not in the breakdown`].filter(Boolean);
                    return { line: i + 2, key: `${c.pbs ? c.pbs + " / " : ""}${c.dbs}${c.wbs ? " / " + c.wbs : ""}`, status: bad.length ? "error" : "new", detail: bad.length ? bad.join("; ") : `${fmtNum(c.hours)} h` };
                  }),
                  commit: () => setCells({ projectId: project._id, cells, replace: true }),
                };
              }}
            />
          )}
        </>
      }
    >
      {!nodes.dbs.length ? (
        <p className="text-sm text-slate-500">Add DBS codes in Breakdowns first.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="text-sm">
            <thead className="text-xs text-slate-500">
              <tr>
                <th className="px-2 py-1 text-left">DBS \ WBS</th>
                {wbsCols.map((w) => (
                  <th key={w} className="border-x border-slate-100 px-2 py-1 text-center">
                    {w || "(no WBS)"}
                  </th>
                ))}
                <th className="bg-slate-50 px-2 py-1 text-center">Total</th>
              </tr>
            </thead>
            <tbody>
              {nodes.dbs.map((d) => (
                <tr key={d.code} className="border-t border-slate-100">
                  <td className="whitespace-nowrap px-2 py-1">
                    <b>{d.code}</b> <span className="text-slate-500">{d.title}</span>
                  </td>
                  {wbsCols.map((w) => {
                    const co = val(cos, d.code, w);
                    return (
                      <td key={w} className="border-x border-slate-100 px-1 py-0.5 text-center">
                        {canEdit && !locked && pbs !== "*" ? (
                          <input
                            aria-label={`Budget ${d.code} ${w}`}
                            className="num h-7 w-24 rounded border border-slate-400 bg-surface px-1 text-center hover:border-slate-500 focus:border-brand-500 focus:outline-none"
                            defaultValue={val(orig, d.code, w) || ""}
                            key={`${pbs}-${val(orig, d.code, w)}`}
                            disabled={!pbs && b.budgetCells.some((c) => c.pbs && c.dbs === d.code && c.wbs === w)}
                            title={!pbs && b.budgetCells.some((c) => c.pbs) ? "This budget is split by phase; pick a phase to edit" : undefined}
                            onBlur={(e) => {
                              const v = e.target.value.trim() === "" ? 0 : Number(e.target.value.replace(/,/g, ""));
                              if (v !== val(orig, d.code, w)) void run(() => setCells({ projectId: project._id, cells: [{ pbs: pbs || undefined, dbs: d.code, wbs: w, hours: v }] }));
                            }}
                          />
                        ) : (
                          <span className="num">{fmtNum(val(orig, d.code, w)) }</span>
                        )}
                        {co !== 0 && <div className="num text-[11px] text-violet-700">CO {co > 0 ? "+" : ""}{fmtNum(co)}</div>}
                      </td>
                    );
                  })}
                  <td className="num bg-slate-50 px-2 py-1 text-center font-medium">{fmtNum(rowTotal(d.code))}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-slate-200 bg-slate-50 font-medium">
                <td className="px-2 py-1">Total</td>
                {wbsCols.map((w) => (
                  <td key={w} className="num border-x border-slate-100 px-2 py-1 text-center">
                    {fmtNum(colTotal(w))}
                  </td>
                ))}
                <td className="num px-2 py-1 text-center">{fmtNum(grand)}</td>
              </tr>
            </tbody>
          </table>
          <p className="mt-3 text-sm text-slate-600">
            Original {fmtNum(origTotal)} h + approved change orders {fmtNum(coTotal)} h = revised {fmtNum(origTotal + coTotal)} h
          </p>
        </div>
      )}
    </Card>
  );
}

/** Rule 11: the budget table must equal the sum of the deliverables. */
function Check() {
  const { b, data, periods, currentIdx } = useProject();
  const { orig, cos } = useCurrentBudget();
  const byWbs = b.budgetCells.some((c) => c.wbs);
  const rows = useMemo(() => {
    const k = (dbs: string, wbs: string) => (byWbs ? `${dbs}|${wbs}` : dbs);
    const budget = new Map<string, number>();
    for (const m of [orig, cos])
      for (const [key, v] of m) {
        const [, d, w] = key.split("|");
        budget.set(k(d, w), (budget.get(k(d, w)) ?? 0) + v);
      }
    const dlv = new Map<string, number>();
    const idx = Math.max(0, currentIdx);
    for (const d of data.deliverables) {
      if ((d.unit ?? "h") !== "h") continue;
      const w = periods.length ? d.weightChanges?.filter((c) => c.periodNumber <= (periods[idx]?.number ?? 0)).at(-1)?.weight ?? d.weight : currentWeight(d);
      const key = k(d.codes.dbs ?? "", d.codes.wbs ?? "");
      dlv.set(key, (dlv.get(key) ?? 0) + w);
    }
    return [...new Set([...budget.keys(), ...dlv.keys()])].sort().map((key) => ({ key, budget: budget.get(key) ?? 0, dlv: dlv.get(key) ?? 0 }));
  }, [orig, cos, data, byWbs, periods, currentIdx]);
  const off = rows.filter((r) => Math.abs(r.budget - r.dlv) > 0.005);
  return (
    <Card title={<>Budget vs deliverables {off.length ? <Badge tone="red">{off.length} mismatches</Badge> : <Badge tone="green">matches</Badge>}</>}>
      <p className="mb-2 text-xs text-slate-500">Current budget (incl. approved COs) compared with the sum of deliverable weights (hours), by DBS{byWbs ? " and WBS" : ""}.</p>
      <table className="text-sm">
        <thead className="text-xs text-slate-500">
          <tr>
            <th className="px-3 py-1 text-left">{byWbs ? "DBS / WBS" : "DBS"}</th>
            <th className="px-3 py-1 text-right">Budget</th>
            <th className="px-3 py-1 text-right">Deliverables</th>
            <th className="px-3 py-1 text-right">Difference</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const diff = r.budget - r.dlv;
            return (
              <tr key={r.key} className={cn("border-t border-slate-100", Math.abs(diff) > 0.005 && "bg-red-50")}>
                <td className="px-3 py-1">{r.key.replace("|", " / ") || "(no DBS)"}</td>
                <td className="num px-3 py-1 text-right">{fmtNum(r.budget, 1)}</td>
                <td className="num px-3 py-1 text-right">{fmtNum(r.dlv, 1)}</td>
                <td className={cn("num px-3 py-1 text-right", Math.abs(diff) > 0.005 && "font-semibold text-red-700")}>{fmtNum(diff, 1)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}

function ChangeOrders() {
  const { project, b, canEdit, isAdmin, periods } = useProject();
  const decide = useMutation(api.budget.decideChangeOrder);
  const importCos = useMutation(api.budget.importChangeOrders);
  const del = useMutation(api.budget.deleteChangeOrder);
  const run = useRun();
  const [edit, setEdit] = useState<Partial<Doc<"changeOrders">> | null>(null);
  const dById = new Map(b.deliverables.map((d) => [d._id, d]));
  const { data } = useProject();
  // Weight before this CO: the latest approved change before it, else the original weight.
  const weightBefore = (c: Doc<"changeOrders">) => {
    const d = data.deliverables.find((x) => x.id === c.deliverableId);
    if (!d) return undefined;
    const prior = (d.weightChanges ?? []).filter((w) => w.ref !== c.number).filter((w) => c.status !== "approved" || w.periodNumber <= c.periodNumber);
    return prior.length ? prior.at(-1)!.weight : d.weight;
  };
  return (
    <Card
      title="Change orders"
      actions={
        <>
          <ExportButton
            file={`${project.code}-change-orders.xlsx`}
            sheets={() => [
              exportChangeOrders(
                b.changeOrders.map((c) => ({
                  number: c.number,
                  kind: c.kind,
                  description: c.description,
                  dbs: c.dbs,
                  wbs: c.wbs,
                  pbs: c.pbs,
                  delta: c.delta,
                  deliverableCode: c.deliverableId ? dById.get(c.deliverableId)?.code : undefined,
                  newWeight: c.newWeight,
                  status: c.status,
                  periodNumber: c.periodNumber,
                })),
              ),
            ]}
          />
          {canEdit && (
            <ImportButton
              build={(sheets, fileName) => {
                const prefix = `CO-${fileName.replace(/\.xlsx$/i, "").replace(/[^A-Za-z0-9]+/g, "").slice(0, 12) || "IMPORT"}`;
                const { items, errors } = parseChangeOrders(sheets[0].rows, prefix);
                const byNumber = new Map(b.changeOrders.map((c) => [c.number, c]));
                const codes = new Set(b.deliverables.map((d) => d.code));
                return {
                  problems: errors.map((e) => `Row ${e.line}: ${e.message}`),
                  note: <p className="text-sm text-slate-600">Imported change orders come in as pending. Approve them here; they take effect in the period they're approved.</p>,
                  rows: items.map((c, i) => {
                    const ex = byNumber.get(c.number);
                    const bad = ex && ex.status !== "pending" ? `already ${ex.status}` : c.kind === "weight" && !codes.has(c.deliverableCode ?? "") ? `deliverable ${c.deliverableCode ?? "(blank)"} not found` : "";
                    return {
                      line: i + 2,
                      key: c.number,
                      status: bad ? "error" : ex ? "changed" : "new",
                      detail: bad || (c.kind === "budget" ? `Budget ${[c.dbs, c.wbs, c.pbs].filter(Boolean).join(" / ")} ${c.delta! > 0 ? "+" : ""}${c.delta} h` : `${c.deliverableCode} → ${c.newWeight}`),
                    };
                  }),
                  commit: () => importCos({ projectId: project._id, rows: items }),
                };
              }}
            />
          )}
          {canEdit && (
            <Button variant="primary" onClick={() => setEdit({ kind: "weight" })}>
              <Plus className="size-4" /> New change order
            </Button>
          )}
        </>
      }
    >
      <p className="mb-3 text-xs text-slate-500">
        Two kinds: a budget table change (discipline level) or a deliverable weight change. When approved, it takes effect in the current open period. For a weight change, the remaining milestones are valued at the new weight × chain %; the
        past stays as it was, so % complete drops (sawtooth) until the next milestone is earned.
      </p>
      <table className="w-full text-sm">
        <thead className="text-left text-xs text-slate-500">
          <tr>
            <th className="py-1">CO</th>
            <th>Kind</th>
            <th>Applies to</th>
            <th className="text-right">Change</th>
            <th>Effective</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {b.changeOrders.map((c) => {
            const d = c.deliverableId ? dById.get(c.deliverableId) : undefined;
            return (
              <tr key={c._id} className="border-t border-slate-100">
                <td className="py-1.5 font-medium">
                  {c.number}
                  {c.description && <div className="text-xs font-normal text-slate-500">{c.description}</div>}
                </td>
                <td>{c.kind === "budget" ? "Budget table" : "Deliverable weight"}</td>
                <td>{c.kind === "budget" ? [c.pbs, c.dbs, c.wbs].filter(Boolean).join(" / ") : d?.code ?? "(deleted)"}</td>
                <td className="num text-right">{c.kind === "budget" ? `${(c.delta ?? 0) > 0 ? "+" : ""}${fmtNum(c.delta)} h` : `${fmtNum(weightBefore(c))} → ${fmtNum(c.newWeight)}`}</td>
                <td>{c.status === "approved" ? `P${c.periodNumber}` : ""}</td>
                <td>
                  <Badge tone={c.status === "approved" ? "green" : c.status === "rejected" ? "slate" : "amber"}>{c.status}</Badge>
                </td>
                <td className="text-right">
                  {c.status === "pending" && isAdmin && (
                    <>
                      <Button size="sm" variant="primary" onClick={() => confirm(`Approve ${c.number}? It takes effect in P${periods.find((p) => !p.closed)?.number}.`) && run(() => decide({ id: c._id, decision: "approved" }), `${c.number} approved`)}>
                        Approve
                      </Button>{" "}
                      <Button size="sm" onClick={() => run(() => decide({ id: c._id, decision: "rejected" }))}>
                        Reject
                      </Button>
                    </>
                  )}
                  {c.status === "pending" && canEdit && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => setEdit(c)}>
                        Edit
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => run(() => del({ id: c._id }))}>
                        Delete
                      </Button>
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {!b.changeOrders.length && <p className="py-4 text-center text-sm text-slate-500">No change orders.</p>}
      {edit && <CoEditor init={edit} onClose={() => setEdit(null)} projectId={project._id} />}
    </Card>
  );
}

function CoEditor({ init, onClose, projectId }: { init: Partial<Doc<"changeOrders">>; onClose: () => void; projectId: Id<"projects"> }) {
  const { b, nodes } = useProject();
  const save = useMutation(api.budget.saveChangeOrder);
  const run = useRun();
  const [f, setF] = useState({
    number: init.number ?? `CO-${String(b.changeOrders.length + 1).padStart(3, "0")}`,
    kind: init.kind ?? ("weight" as "weight" | "budget"),
    description: init.description ?? "",
    dbs: init.dbs ?? "",
    wbs: init.wbs ?? "",
    pbs: init.pbs ?? "",
    delta: init.delta !== undefined ? String(init.delta) : "",
    deliverableId: (init.deliverableId ?? "") as string,
    newWeight: init.newWeight !== undefined ? String(init.newWeight) : "",
  });
  const d = b.deliverables.find((x) => x._id === f.deliverableId);
  const { data } = useProject();
  const ed = data.deliverables.find((x) => x.id === f.deliverableId);
  return (
    <Modal
      open
      onClose={onClose}
      title={init._id ? `Edit ${init.number}` : "New change order"}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={async () => {
              const args =
                f.kind === "budget"
                  ? { dbs: f.dbs || undefined, wbs: f.wbs, pbs: f.pbs || undefined, delta: f.delta === "" ? undefined : Number(f.delta) }
                  : { deliverableId: (f.deliverableId || undefined) as Id<"deliverables"> | undefined, newWeight: f.newWeight === "" ? undefined : Number(f.newWeight) };
              const ok = await run(() => save({ projectId, id: init._id, number: f.number, kind: f.kind, description: f.description || undefined, ...args }), "Change order saved (pending approval)");
              if (ok !== undefined) onClose();
            }}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="CO number">
          <Input value={f.number} onChange={(e) => setF({ ...f, number: e.target.value })} />
        </Field>
        <Field label="Kind">
          <Select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value as "weight" })}>
            <option value="weight">Deliverable weight</option>
            <option value="budget">Budget table (discipline)</option>
          </Select>
        </Field>
        <Field label="Description" className="col-span-2">
          <Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
        </Field>
        {f.kind === "budget" ? (
          <>
            <Field label="DBS">
              <Select value={f.dbs} onChange={(e) => setF({ ...f, dbs: e.target.value })}>
                <option value="" />
                {nodes.dbs.map((n) => (
                  <option key={n.code}>{n.code}</option>
                ))}
              </Select>
            </Field>
            <Field label="WBS">
              <Select value={f.wbs} onChange={(e) => setF({ ...f, wbs: e.target.value })}>
                <option value="">(no WBS)</option>
                {nodes.wbs.map((n) => (
                  <option key={n.code}>{n.code}</option>
                ))}
              </Select>
            </Field>
            <Field label="PBS (optional)">
              <Select value={f.pbs} onChange={(e) => setF({ ...f, pbs: e.target.value })}>
                <option value="" />
                {nodes.pbs.map((n) => (
                  <option key={n.code}>{n.code}</option>
                ))}
              </Select>
            </Field>
            <Field label="Change (hours, + or -)">
              <Input type="number" value={f.delta} onChange={(e) => setF({ ...f, delta: e.target.value })} />
            </Field>
            <p className="col-span-2 text-xs text-amber-700">The budget table has to match the deliverables, so pair this with the matching deliverable weight change.</p>
          </>
        ) : (
          <>
            <Field label="Deliverable" className="col-span-2">
              <Select value={f.deliverableId} onChange={(e) => setF({ ...f, deliverableId: e.target.value })}>
                <option value="" />
                {b.deliverables.map((x) => (
                  <option key={x._id} value={x._id}>
                    {x.code} · {x.title}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Current weight">
              <Input value={ed ? currentWeight(ed) : ""} disabled />
            </Field>
            <Field label="New weight">
              <Input type="number" min={0} value={f.newWeight} onChange={(e) => setF({ ...f, newWeight: e.target.value })} />
            </Field>
            {d && d.steps.length > 0 && d.steps.every((s) => s.earned) && <p className="col-span-2 text-xs text-amber-700">This deliverable is already 100% earned. How its earned value should move is a question pending with Cesar; for now it won't change.</p>}
          </>
        )}
      </div>
    </Modal>
  );
}
